/* drops.js — Sneak Freak (AphCom) · drops feature
   <sf-release-schedule>  month grid + day list from the JSON blob rendered by snippets/drops-data (release calendar
                          on the drops page and, in compact mode, inside the #drop-calendar dialog).
   <sf-notify-sheet>      the "Remind me" sheet body: fills the product row from `remind:open`, builds the .ics link,
                          tags the customer form (email mode), saves the on-device reminder, publishes `remind:saved`.
   <sf-next-drop>         re-checks the spotlight against the blob (Liquid `now` is cache-stale) and shows the live note.
   Ports of the prototype's account.js sfInitReleaseCalendar/sfInitCountdown and notify.js. Requires theme.js. */
'use strict';
(function () {
  const theme = window.theme || (window.theme = {});
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const pad = (n) => String(n).padStart(2, '0');
  const store = {
    get(k, fb) { try { return theme.storage ? theme.storage.get(k, fb) : fb; } catch (e) { return fb; } },
    set(k, v) { try { if (theme.storage) theme.storage.set(k, v); } catch (e) { /* storage blocked */ } }
  };
  const readBlob = (root) => {
    const el = root.querySelector('script[data-drops]') || document.querySelector('script[data-drops]');
    if (!el) return [];
    try { const list = JSON.parse(el.textContent); return Array.isArray(list) ? list : []; } catch (e) { return []; }
  };
  const nowSec = () => Math.floor(Date.now() / 1000);
  /** "YYYY-MM-DD" → {y, m(0-11), d} */
  const parseDay = (s) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s || ''); return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null; };
  const dayKey = (y, m, d) => y + '-' + pad(m + 1) + '-' + pad(d);
  const reminders = () => { const l = store.get('reminders', []); return Array.isArray(l) ? l : []; };
  const hasReminder = (handle) => reminders().some((r) => r && r.handle === handle);

  /* ================================================================ sf-release-schedule */
  class SFReleaseSchedule extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.i18n = {};
      try { this.i18n = JSON.parse(this.dataset.i18n || '{}'); } catch (e) { this.i18n = {}; }
      this.weekStart = this.dataset.weekStart === 'monday' ? 1 : 0;
      this.monthsAhead = parseInt(this.dataset.monthsAhead, 10) || 3;
      this.showBell = this.hasAttribute('data-show-bell');
      this.items = readBlob(this).filter((p) => p && p.day && p.epoch);
      this.body = this.querySelector('[data-cal-body]');
      this.grid = this.querySelector('[data-cal-grid]');
      this.monthEl = this.querySelector('[data-cal-month]');
      this.dow = this.querySelector('[data-cal-dow]');
      this.list = this.querySelector('[data-cal-list]');
      this.fallback = this.querySelector('[data-fallback]');
      if (!this.body || !this.grid || !this.list) return;
      if (!this.items.length) { this.list.innerHTML = '<p class="sched-cal__empty">' + esc(this.i18n.no_upcoming || '') + '</p>'; return; }

      this.byDay = {};
      this.items.forEach((p) => { (this.byDay[p.day] = this.byDay[p.day] || []).push(p); });
      const now = nowSec();
      const upcoming = this.items.filter((p) => p.epoch >= now);
      const first = upcoming[0] || this.items[this.items.length - 1];
      const fd = parseDay(first.day);
      const today = new Date();
      this.minMonth = { y: today.getFullYear(), m: today.getMonth() };
      this.view = { y: fd.y, m: fd.m };
      this.selected = first.day;

      this.grid.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-day]');
        if (!btn) return;
        this.selected = btn.dataset.day;
        this.grid.querySelectorAll('.sched-day').forEach((b) => { b.classList.remove('is-active'); b.setAttribute('aria-selected', 'false'); });
        btn.classList.add('is-active');
        btn.setAttribute('aria-selected', 'true');
        this.renderList();
      });
      this.grid.addEventListener('keydown', (e) => this.onGridKey(e));
      const prev = this.querySelector('[data-cal-prev]'), next = this.querySelector('[data-cal-next]');
      if (prev) prev.addEventListener('click', () => this.shift(-1));
      if (next) next.addEventListener('click', () => this.shift(1));
      this.renderDow();
      this.render();
      this.body.hidden = false;
      if (this.fallback) this.fallback.hidden = true;
      this._unsub = theme.pubsub ? theme.pubsub.subscribe('remind:saved', () => this.renderList()) : null;
    }
    disconnectedCallback() { if (this._unsub) this._unsub(); }
    shift(dir) {
      const v = { y: this.view.y, m: this.view.m + dir };
      if (v.m < 0) { v.m = 11; v.y--; } if (v.m > 11) { v.m = 0; v.y++; }
      this.view = v;
      this.render();
    }
    monthIndex(v) { return v.y * 12 + v.m; }
    renderDow() {
      if (!this.dow) return;
      const names = this.i18n.days || ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
      let html = '';
      for (let i = 0; i < 7; i++) html += '<span>' + esc(names[(i + this.weekStart) % 7]) + '</span>';
      this.dow.innerHTML = html;
    }
    render() {
      const months = this.i18n.months || ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
      const { y, m } = this.view;
      if (this.monthEl) this.monthEl.textContent = months[m] + ' ' + y;
      const firstDow = (new Date(y, m, 1).getDay() - this.weekStart + 7) % 7;
      const days = new Date(y, m + 1, 0).getDate();
      const today = new Date();
      const todayKey = dayKey(today.getFullYear(), today.getMonth(), today.getDate());
      let cells = '';
      for (let i = 0; i < firstDow; i++) cells += '<span class="sched-day sched-day--pad" aria-hidden="true"></span>';
      for (let d = 1; d <= days; d++) {
        const key = dayKey(y, m, d);
        const list = this.byDay[key];
        const has = !!list;
        const isToday = key === todayKey;
        const label = has ? (d + ' ' + months[m] + ', ' + (list.length === 1 ? this.i18n.releases_one : (this.i18n.releases_other || '').replace(/\d+/, String(list.length)))) : '';
        cells += '<button type="button" role="gridcell" class="sched-day' + (has ? ' sched-day--has' : '') + (isToday ? ' sched-day--today' : '') + (has && key === this.selected ? ' is-active' : '') + '"' +
          (has ? ' data-day="' + key + '" aria-selected="' + (key === this.selected) + '" aria-label="' + esc(label) + '"' : ' disabled aria-disabled="true" tabindex="-1"') +
          (has && key !== this.selected ? ' tabindex="-1"' : '') + '>' + d + (has ? '<span class="sched-day__dot" aria-hidden="true"></span>' : '') + '</button>';
      }
      this.grid.innerHTML = cells;
      const prev = this.querySelector('[data-cal-prev]'), next = this.querySelector('[data-cal-next]');
      if (prev) prev.disabled = this.monthIndex(this.view) <= this.monthIndex(this.minMonth) - 12;
      if (next) next.disabled = this.monthIndex(this.view) >= this.monthIndex(this.minMonth) + this.monthsAhead;
      this.renderList();
    }
    onGridKey(e) {
      const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
      if (!keys.includes(e.key)) return;
      const days = Array.from(this.grid.querySelectorAll('[data-day]'));
      if (!days.length) return;
      const cur = days.indexOf(document.activeElement);
      let next = cur;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = Math.min(days.length - 1, cur + 1);
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = Math.max(0, cur - 1);
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = days.length - 1;
      e.preventDefault();
      days[next].focus();
      days[next].click();
    }
    renderList() {
      const items = this.byDay[this.selected] || [];
      const now = nowSec();
      if (!items.length) { this.list.innerHTML = '<p class="sched-cal__empty">' + esc(this.i18n.no_releases || '') + '</p>'; return; }
      const months = this.i18n.months || [];
      const weekdays = this.i18n.weekdays || [];
      const pd = parseDay(this.selected);
      const dateObj = new Date(pd.y, pd.m, pd.d);
      const label = (weekdays[dateObj.getDay()] ? weekdays[dateObj.getDay()] + ' ' : '') + pd.d + ' ' + (months[pd.m] || '') + ' ' + pd.y;
      this.list.innerHTML = '<p class="sched-cal__date">' + esc(label) + '</p>' + items.map((p) => {
        const live = p.epoch <= now;
        const set = hasReminder(p.handle);
        const bell = this.showBell && !live ? (
          '<sf-remind-button class="sched-item__remind' + (set ? ' is-set' : '') + '" data-handle="' + esc(p.handle) + '" data-title="' + esc(p.title) + '" data-vendor="' + esc(p.vendor) + '" data-url="' + esc(p.url) + '" data-release="' + esc(p.epoch) + '" data-image="' + esc(p.image) + '" data-price="' + esc(p.price) + '">' +
            '<button type="button" class="icon-btn sched-item__bell' + (set ? ' is-set' : '') + '" aria-pressed="' + set + '" aria-label="' + esc((this.i18n.remind || 'Remind me') + ': ' + p.title) + '">' +
              '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.5 18h15L18 15.5Z"/><path d="M10 20.5a2.2 2.2 0 0 0 4 0"/></svg>' +
            '</button></sf-remind-button>') : '';
        return '<div class="sched-item' + (live ? ' sched-item--live' : '') + '">' +
          '<a class="sched-item__link" href="' + esc(p.url) + '">' +
            '<span class="sched-item__thumb">' + (p.image ? '<img class="sched-item__img" src="' + esc(p.image) + '" alt="" width="64" height="64" loading="lazy">' : '') + '</span>' +
            '<span class="sched-item__text">' +
              '<span class="sched-item__brand">' + esc(p.vendor) + '</span>' +
              '<span class="sched-item__name">' + esc(p.title) + '</span>' +
              '<span class="sched-item__time">' + (live ? esc(this.i18n.live || 'Live now') : esc(p.time)) + '</span>' +
            '</span>' +
            '<span class="sched-item__price">' + esc(p.price) + '</span>' +
          '</a>' + bell + '</div>';
      }).join('');
    }
  }
  if (!customElements.get('sf-release-schedule')) customElements.define('sf-release-schedule', SFReleaseSchedule);

  /* ================================================================ sf-notify-sheet */
  class SFNotifySheet extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.mode = this.dataset.mode || 'email';
      this.current = null;
      this.onOpen = (e) => this.fill(e.detail || {});
      document.addEventListener('remind:open', this.onOpen);
      const dev = this.querySelector('[data-notify-device]');
      if (dev) dev.addEventListener('click', () => this.saveDevice(true));
      const form = this.querySelector('[data-notify-form]');
      /* the device copy of the reminder is written when Shopify confirms the sign-up (below), not on submit — a
         rejected address must not leave a reminder behind that the shopper believes is on its way */
      if (form) form.addEventListener('submit', () => {
        form.classList.add('is-loading');
        try { sessionStorage.setItem('sf-remind-submitted', JSON.stringify({ d: this.current, t: Date.now() })); } catch (e) { /* storage blocked: nothing is saved, the server still has it */ }
      });
      const link = this.querySelector('[data-notify-link]');
      if (link) link.addEventListener('click', () => this.saveDevice(false));
      /* After the customer form posts (page reload with ?form_type=customer&contact_posted=true), re-open and restore. */
      const posted = this.querySelector('[data-notify-posted]');
      /* a newsletter sign-up also lands here as "posted" (see theme.ownsCustomerPost): it must not re-open this sheet
         on a stale product and mark it saved, and the sheet keeps its form ready for the next drop */
      const own = typeof theme.ownsCustomerPost === 'function' ? theme.ownsCustomerPost('notify-form') : null;
      if (own === false) {
        this.querySelectorAll('[data-notify-posted], [data-notify-error]').forEach((n) => n.remove());
        const fields = this.querySelector('[data-notify-fields]');
        if (fields) fields.hidden = false;
      } else if (posted) {
        /* save only what this tab really sent, and only once; the merely-opened product is never saved */
        /* and only on the load the submission itself produced: reopening an old success URL must not save anything */
        const sent = this.takeSubmitted();
        if (sent && theme.customerPostRecord === 'notify-form') { this.fill(sent); this.saveDevice(false); store.set('remind-pending', null); }
        const sheet = this.closest('sf-drawer');
        if (sheet && typeof sheet.open === 'function') setTimeout(() => sheet.open(), 50);
      } else if (own === true && this.querySelector('[data-notify-error]')) {
        /* this form's own rejection: show it, on the product it was for, instead of leaving it in a closed sheet; the
           record stays so the corrected retry from this sheet still knows the product */
        /* spent here too: fill() keeps the product for the retry, whose own submit records it again */
        const sent = this.takeSubmitted() || store.get('remind-pending', null);
        if (sent && sent.handle) this.fill(sent);
        const sheet = this.closest('sf-drawer');
        if (sheet && typeof sheet.open === 'function') setTimeout(() => sheet.open(), 50);
      }
    }
    disconnectedCallback() { document.removeEventListener('remind:open', this.onOpen); }
    /** the product this tab submitted, if the record is fresh (it outlives a bot-protection challenge page) */
    peekSubmitted() {
      try {
        const rec = JSON.parse(sessionStorage.getItem('sf-remind-submitted') || 'null');
        return rec && rec.d && rec.d.handle && Date.now() - rec.t < 10 * 60 * 1000 ? rec.d : null;
      } catch (e) { return null; }
    }
    takeSubmitted() {
      const d = this.peekSubmitted();
      try { sessionStorage.removeItem('sf-remind-submitted'); } catch (e) { /* nothing to clear */ }
      return d;
    }
    fill(d) {
      this.current = d;
      const row = this.querySelector('[data-notify-product]');
      const img = this.querySelector('[data-notify-image]');
      const set = (sel, val) => { const el = this.querySelector(sel); if (el) el.textContent = val || ''; };
      set('[data-notify-vendor]', d.vendor);
      set('[data-notify-title]', d.title);
      set('[data-notify-price]', d.price);
      if (img) { if (d.image) { img.src = d.image; img.alt = [d.vendor, d.title].filter(Boolean).join(' '); img.hidden = false; } else { img.hidden = true; img.removeAttribute('src'); } }
      if (row) row.hidden = !(d.title || d.vendor);
      const rel = this.releaseDate(d.release);
      const relRow = this.querySelector('[data-notify-release-row]');
      const relEl = this.querySelector('[data-notify-release]');
      if (relEl) relEl.textContent = rel ? this.formatRelease(rel) : '';
      if (relRow) relRow.hidden = !rel;
      const tags = this.querySelector('[data-remind-tags]');
      if (tags) tags.value = (this.closest('sf-drawer') && this.closest('sf-drawer').dataset.baseTag || 'drop-reminder') + (d.handle ? ',drop:' + d.handle : '');
      const note = this.querySelector('[data-remind-note]');
      if (note) note.value = [d.vendor, d.title].filter(Boolean).join(' ') + (rel ? ' — ' + rel.toISOString() : '') + (d.url ? ' — ' + new URL(d.url, location.origin).href : '');
      this.buildIcs(d, rel);
      const state = this.querySelector('[data-notify-device-state]');
      if (state) state.hidden = !hasReminder(d.handle);
      if (this.mode === 'email') store.set('remind-pending', { handle: d.handle, title: d.title, vendor: d.vendor, release: d.release, image: d.image, url: d.url, price: d.price });
    }
    releaseDate(raw) {
      if (raw === undefined || raw === null || raw === '') return null;
      const n = Number(raw);
      if (Number.isFinite(n) && n > 0) return new Date(n < 1e12 ? n * 1000 : n);
      const d = new Date(raw);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    formatRelease(d) {
      try {
        return new Intl.DateTimeFormat(document.documentElement.lang || undefined, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
      } catch (e) { return d.toLocaleString(); }
    }
    buildIcs(d, rel) {
      const a = this.querySelector('[data-notify-ics]');
      if (!a) return;
      if (!rel) { a.hidden = true; return; }
      const stamp = (x) => x.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const end = new Date(rel.getTime() + 30 * 60000);
      const title = [d.vendor, d.title].filter(Boolean).join(' ');
      const url = d.url ? new URL(d.url, location.origin).href : location.origin;
      const ics = ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Sneak Freak//Drops//EN', 'BEGIN:VEVENT', 'UID:' + (d.handle || 'drop') + '@' + location.hostname, 'DTSTAMP:' + stamp(new Date()), 'DTSTART:' + stamp(rel), 'DTEND:' + stamp(end), 'SUMMARY:' + title.replace(/[,;]/g, ' '), 'URL:' + url, 'DESCRIPTION:' + url, 'BEGIN:VALARM', 'TRIGGER:-PT1H', 'ACTION:DISPLAY', 'DESCRIPTION:' + title.replace(/[,;]/g, ' '), 'END:VALARM', 'END:VEVENT', 'END:VCALENDAR'].join('\r\n');
      a.href = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(ics);
      a.download = ((d.handle || 'drop') + '.ics');
      a.hidden = false;
    }
    saveDevice(announce) {
      const d = this.current;
      if (!d || !d.handle) return;
      const rel = this.releaseDate(d.release);
      const list = reminders().filter((r) => r && r.handle !== d.handle);
      list.push({ handle: d.handle, title: d.title, vendor: d.vendor, url: d.url, image: d.image, price: d.price, release: rel ? rel.toISOString() : '', releaseLabel: rel ? this.formatRelease(rel) : '', when: 'hour', saved: Date.now() });
      store.set('reminders', list);
      this.markSaved(d);
      if (theme.pubsub) theme.pubsub.publish('remind:saved', { handle: d.handle, reminder: list[list.length - 1] });
      if (announce) {
        if (theme.toast) theme.toast((theme.strings && theme.strings.reminder_saved) || 'Reminder set', { type: 'success' });
        const sheet = this.closest('sf-drawer');
        if (sheet && typeof sheet.close === 'function') setTimeout(() => sheet.close(), 250);
      }
    }
    markSaved() {
      const state = this.querySelector('[data-notify-device-state]');
      if (state) state.hidden = false;
    }
  }
  if (!customElements.get('sf-notify-sheet')) customElements.define('sf-notify-sheet', SFNotifySheet);

  /* ================================================================ sf-next-drop */
  class SFNextDrop extends HTMLElement {
    connectedCallback() {
      const epoch = parseInt(this.dataset.epoch, 10);
      const live = this.querySelector('[data-live-note]');
      const check = () => {
        const isLive = Number.isFinite(epoch) && epoch > 0 && epoch * 1000 <= Date.now();
        this.classList.toggle('is-live', isLive);
        if (live) live.hidden = !isLive;
      };
      check();
      this._timer = setInterval(check, 30000);
      this.addEventListener('countdown:live', check);
    }
    disconnectedCallback() { clearInterval(this._timer); }
  }
  if (!customElements.get('sf-next-drop')) customElements.define('sf-next-drop', SFNextDrop);
})();
